import React, { useEffect, useMemo, useState } from "react";
import { useIdleExempt } from "../hooks/useIdleExempt";
import axios from "axios";
import { toast } from "sonner";
import {
  Home, RefreshCw, Search, Loader2, ArrowUp, ArrowDown, Check, X, Plus, Upload,
  Pencil, PowerOff, Undo2, Trash2, AlertTriangle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// 2026-02-28 (Dave) — Admin → Associations card.
//
// Flat cross-firm list of every association the current admin can
// administer. Platform Admins see every association; Firm Admins see
// only associations under the firms they administer (enforced by
// GET /api/admin/associations).
//
// UX intent: one screen the CPA can open, search "adelaide" or
// "whca", spot the row, click through directly to that
// association's Dashboard — no drilling into Firms first.

function StatusPill({ active }) {
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] tracking-[0.05em] font-semibold rounded-[3px]"
      style={{
        background: active ? "#EAF6EF" : "#F1F3F5",
        color: active ? "#2E7D5B" : "#5A6B75",
        border: `1px solid ${active ? "#2E7D5B" : "#B8C1CB"}`,
      }}
      data-testid={`associations-admin-status-${active ? "active" : "inactive"}`}
    >
      {active ? <Check size={9} strokeWidth={2.5} /> : <X size={9} strokeWidth={2.5} />}
      {active ? "Active" : "Inactive"}
    </span>
  );
}

// 2026-03-04 (Dave) — Small chip that renders the association's content
// tier at a glance. Navy for Pro (fuller feature set); coral for Basic
// so a quick scan of the Admin → Associations grid or a Firm drawer
// immediately reveals which associations are on the trimmed layout.
export function TierChip({ tier }) {
  const t = (tier || "pro").toLowerCase();
  const isPro = t === "pro";
  return (
    <span
      className="inline-flex items-center justify-center w-[52px] px-1.5 py-0.5 text-[9.5px] tracking-[0.10em] uppercase font-bold rounded-[3px]"
      style={{
        background: isPro ? "#EAF0F7" : "#FEF1EC",
        color:      isPro ? "#1C4E80" : "#B24E32",
        border:     `1px solid ${isPro ? "#1C4E80" : "#EA6A47"}`,
      }}
      data-testid={`tier-chip-${t}`}
    >
      {isPro ? "Pro" : "Basic"}
    </span>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { timeZone: "America/Chicago", year: "numeric", month: "short", day: "numeric" });
}

export default function AssociationsAdminScreen({ onSelectAid, onScreenChange }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [firmFilter, setFirmFilter] = useState("");
  // 2026-03-04 (Dave) — Per-tier filter ("" = all, "pro" / "basic").
  // Sits alongside firmFilter as a lightweight chip trio so admins can
  // spot every Basic client at a glance.
  const [tierFilter, setTierFilter] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  // 2026-02-28 (Dave) — `editing` holds the row currently being edited,
  // or null when the modal is closed.  Clicking anywhere on a row
  // opens the edit modal; the modal PATCHes /admin/associations/{aid}
  // with only the fields that changed and closes on save.
  const [editing, setEditing] = useState(null);
  // 2026-02-28 (Dave, v3) — mirror the FirmsScreen drawer's action set:
  // in addition to the pencil we expose (De)Activate + Delete
  // Permanently.  `busyAid` blocks per-row double-clicks while the
  // soft-toggle round-trip is in flight; `purging` holds the row
  // pending irreversible deletion (typed-name confirmation modal).
  const [busyAid, setBusyAid] = useState(null);
  const [purging, setPurging] = useState(null);

  // Sort state — default: firm A→Z, then name A→Z (matches API).
  const [sortKey, setSortKey] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const toggleSort = (key) => {
    if (key === sortKey) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  };

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const r = await axios.get(`${API}/admin/associations`, { params: { include_inactive: true } });
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr((typeof detail === "string" && detail) || e.message || "Failed to load associations.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Distinct firm names for the firm-filter dropdown.
  const firmOptions = useMemo(() => {
    const s = new Set(rows.map((r) => r.tenant_name).filter(Boolean));
    return [...s].sort((a, b) => a.localeCompare(b));
  }, [rows]);

  // Filter + sort in-browser (dataset is small — typical firms hold
  // low tens of associations, well under 1k total for the platform).
  const sortedRows = useMemo(() => {
    const accessors = {
      name:  (r) => (r.name || "").toLowerCase(),
      firm:  (r) => (r.tenant_name || "").toLowerCase(),
      type:  (r) => (r.association_type || "").toLowerCase(),
      id:    (r) => (r.tenant_display_id || ""),
      units: (r) => (typeof r.unit_count === "number" ? r.unit_count : -1),
      users: (r) => (typeof r.user_count === "number" ? r.user_count : -1),
      status:(r) => (r.is_active ? 1 : 0),
      created: (r) => (r.created_at || ""),
    };
    const acc = accessors[sortKey] || accessors.firm;
    const dir = sortDir === "asc" ? 1 : -1;
    const ql = q.trim().toLowerCase();
    const filtered = rows.filter((r) => {
      if (!showInactive && !r.is_active) return false;
      if (firmFilter && r.tenant_name !== firmFilter) return false;
      if (tierFilter && (r.tier || "pro").toLowerCase() !== tierFilter) return false;
      if (!ql) return true;
      return (
        (r.name || "").toLowerCase().includes(ql) ||
        (r.tenant_name || "").toLowerCase().includes(ql) ||
        (r.address || "").toLowerCase().includes(ql) ||
        (r.tenant_display_id || "").toLowerCase().includes(ql)
      );
    });
    return [...filtered].sort((a, b) => {
      const va = acc(a);
      const vb = acc(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
  }, [rows, q, firmFilter, tierFilter, showInactive, sortKey, sortDir]);

  const openAssociation = (aid) => {
    if (typeof onSelectAid === "function") onSelectAid(aid);
    if (typeof onScreenChange === "function") onScreenChange("Dashboard");
  };

  // ---- Row actions: soft-toggle + hard purge -----------------------
  // Endpoints match the ones the FirmsScreen drawer uses so both
  // surfaces stay behaviourally identical (audit event, permission
  // gate, cascade rules all live in the same server code).
  const doSoftToggle = async (aid, currentlyActive) => {
    setBusyAid(aid);
    try {
      if (currentlyActive) {
        await axios.delete(`${API}/admin/associations/${aid}`);
      } else {
        await axios.post(`${API}/admin/associations/${aid}/restore`);
      }
      toast.success(currentlyActive ? "Association deactivated." : "Association reactivated.");
      await load();
      try {
        window.dispatchEvent(new CustomEvent("ureserve:associations-changed"));
      } catch { /* no-op */ }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Failed.");
    } finally {
      setBusyAid(null);
    }
  };

  const doPurge = async (aid, confirmName) => {
    setBusyAid(aid);
    try {
      await axios.delete(`${API}/admin/associations/${aid}/purge`, {
        data: { confirm_name: confirmName },
      });
      toast.success("Association permanently deleted.");
      setPurging(null);
      await load();
      try {
        window.dispatchEvent(new CustomEvent("ureserve:associations-changed"));
      } catch { /* no-op */ }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Delete failed.");
    } finally {
      setBusyAid(null);
    }
  };

  const columns = [
    { key: "name",    label: "Association",  width: 240 },
    { key: "firm",    label: "Firm",         width: 160 },
    // 2026-10-01 (Dave) — Type is captured on the web-enrollment
    // form (HOA / Condo / Marina / …). Narrow width keeps the
    // 10-column grid from spilling horizontally; truncation is OK
    // because the Association Details panel shows the full value.
    { key: "type",    label: "Type",         width: 150 },
    { key: "id",      label: "ID",           width: 70  },
    // 2026-02-28 (Dave) — Units column centered so header + values
    // align cleanly under one another.
    { key: "units",   label: "Units",        width: 90,  align: "center" },
    // 2026-03-08 (Dave) — Users column: count of active users with a
    // client-scope membership on the association. Sits right after
    // Units so the "how big is this book of business" trio (Units →
    // Users → Tier) reads left to right.
    { key: "users",   label: "Users",        width: 80,  align: "center" },
    { key: "tier",    label: "Tier",         width: 80,  align: "center" },
    { key: "status",  label: "Status",       width: 100 },
    { key: "created", label: "Created",      width: 130 },
    // 2026-02-28 (Dave, v2) — trailing Actions column that hosts the
    // per-row edit + soft-toggle + delete buttons.  Matches the
    // FirmsScreen drawer's icon-cluster exactly so admins learn the
    // gesture once and reuse it everywhere.  Wider than the pencil-
    // only column so all three buttons fit comfortably.
    { key: "actions", label: "Actions",     width: 120, align: "center", noSort: true },
  ];

  return (
    <div className="panel-compact flex flex-col" data-testid="screen-associations-admin">
      <div
        className="section-title-sm grid items-baseline"
        style={{ borderBottomColor: "#EA6A47", gridTemplateColumns: "1fr auto 1fr" }}
      >
        <span style={{ fontSize: 20, fontWeight: 700 }} className="inline-flex items-center gap-2 justify-self-start">
          <Home size={18} className="text-navy" strokeWidth={1.8} />
          Associations
        </span>
        <span
          className="text-slate text-[11px] tracking-[0.06em] justify-self-center"
          data-testid="associations-admin-count"
        >
          {sortedRows.length} of {rows.length} {rows.length === 1 ? "association" : "associations"}
        </span>
        {/* 2026-02-28 (Dave) — primary action button-group: filled
            navy Add Association + adjoined Bulk Load. Both use the
            tight 3-px corner radius; grouped visually with a shared
            border so they read as one control. */}
        <div className="inline-flex items-stretch justify-self-end" data-testid="associations-admin-add-group">
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-1 px-3 py-1 text-[11.5px] font-semibold bg-navy text-white border border-navy rounded-l-[3px] hover:bg-[#164066] transition-colors"
            data-testid="associations-admin-add-btn"
          >
            <Plus size={12} strokeWidth={2.4} />
            Add Association
          </button>
          <button
            type="button"
            onClick={() => toast("Coming Soon", { description: "Bulk load for associations is on the roadmap." })}
            className="inline-flex items-center gap-1 px-3 py-1 text-[11.5px] font-semibold bg-navy text-white border border-navy rounded-r-[3px] hover:bg-[#164066] transition-colors"
            style={{ borderLeftColor: "#0091D5", borderLeftWidth: 2 }}
            data-testid="associations-admin-bulk-load-btn"
            title="Bulk import from CSV — coming soon"
          >
            <Upload size={12} strokeWidth={2.4} />
            Bulk Load
          </button>
        </div>
      </div>

      {/* Filter row */}
      <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="associations-admin-filters">
        <div className="relative">
          <Search
            size={12}
            className="absolute left-2 top-1/2 -translate-y-1/2 text-slate"
            strokeWidth={2}
          />
          <input
            type="text"
            placeholder="Search name, firm, address, or ID…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-6 pr-2 py-1 text-[11.5px] border border-rule rounded-[3px] focus:border-navy focus:outline-none"
            style={{ width: 280 }}
            data-testid="associations-admin-search"
          />
        </div>
        <select
          value={firmFilter}
          onChange={(e) => setFirmFilter(e.target.value)}
          className="px-2 py-1 text-[11.5px] border border-rule rounded-[3px] focus:border-navy focus:outline-none"
          data-testid="associations-admin-firm-filter"
        >
          <option value="">All firms</option>
          {firmOptions.map((f) => (
            <option key={f} value={f}>{f}</option>
          ))}
        </select>
        {/* 2026-03-04 (Dave) — Tier filter chip trio. Radio-style: click a
            chip to filter to that tier, click the active one (or "All") to
            clear. Kept as a trio (not a select) so the current filter is
            visible at a glance the same way `Show inactive` is. */}
        <div
          className="inline-flex items-center gap-0.5 border border-rule rounded-[3px] p-0.5"
          data-testid="associations-admin-tier-filter"
        >
          {[
            { key: "",      label: "All"   },
            { key: "pro",   label: "Pro"   },
            { key: "basic", label: "Basic" },
          ].map((opt) => {
            const active = tierFilter === opt.key;
            return (
              <button
                key={opt.key || "all"}
                type="button"
                onClick={() => setTierFilter(active ? "" : opt.key)}
                className={`px-2 py-0.5 text-[10.5px] tracking-[0.06em] uppercase font-bold rounded-[2px] transition-colors ${
                  active ? "bg-navy text-white" : "text-slate hover:text-navy"
                }`}
                data-testid={`associations-admin-tier-filter-${opt.key || "all"}`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
        <label
          className="inline-flex items-center gap-1 text-[11.5px] text-navy cursor-pointer select-none"
          data-testid="associations-admin-toggle-inactive"
        >
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="accent-navy"
          />
          Show inactive
        </label>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-1 px-2 py-1 text-[11.5px] font-semibold border border-navy text-navy rounded-[3px] hover:bg-navy hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="associations-admin-refresh"
        >
          {loading ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
          Refresh
        </button>
      </div>

      {err && (
        <div
          className="mt-3 border border-coral bg-[#FEF5F2] px-3 py-2 text-[12px] text-coral rounded-[3px]"
          data-testid="associations-admin-error"
        >
          {err}
        </div>
      )}

      <div className="mt-3 overflow-auto flex-1 border border-rule rounded-[3px]" data-testid="associations-admin-table-wrap">
        <table className="u-table u-table-dense" style={{ tableLayout: "auto", width: "100%" }} data-testid="associations-admin-table">
          <thead>
            <tr style={{ background: "#1C4E80", color: "#fff" }}>
              {columns.map((c) => {
                const active = c.key === sortKey;
                const Arrow = active && sortDir === "asc" ? ArrowUp : ArrowDown;
                return (
                  <th
                    key={c.key}
                    onClick={c.noSort ? undefined : () => toggleSort(c.key)}
                    data-testid={`associations-admin-th-${c.key}`}
                    aria-sort={active ? (sortDir === "asc" ? "ascending" : "descending") : "none"}
                    style={{
                      padding: "6px 10px",
                      textAlign: c.align || "left",
                      fontSize: 11,
                      letterSpacing: 0.8, textTransform: "uppercase", width: c.width,
                      cursor: c.noSort ? "default" : "pointer", userSelect: "none",
                    }}
                  >
                    <span
                      className="inline-flex items-center gap-1"
                      style={{
                        justifyContent: c.align === "center" ? "center" : undefined,
                      }}
                    >
                      {c.label}
                      {!c.noSort && active && (
                        <Arrow size={11} strokeWidth={2.6} style={{ color: "#EA6A47" }} />
                      )}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 && (
              <tr>
                <td colSpan={columns.length} className="px-3 py-6 text-center text-slate text-[12px]">
                  <Loader2 size={14} className="inline-block animate-spin mr-1.5 -mb-0.5" />
                  Loading…
                </td>
              </tr>
            )}
            {!loading && sortedRows.length === 0 && !err && (
              <tr>
                <td colSpan={columns.length} className="px-3 py-6 text-center text-slate italic text-[12px]" data-testid="associations-admin-empty">
                  No associations match the current filters.
                </td>
              </tr>
            )}
            {sortedRows.map((row) => (
              <tr
                key={row.aid}
                data-testid={`associations-admin-row-${row.aid}`}
                className="hover:bg-navy-wash transition-colors"
                style={{ borderTop: "1px solid #E8ECF1" }}
              >
                <td className="px-2.5 py-1.5 text-[12px] text-navy font-semibold">
                  {row.name}
                  {row.address && (
                    <div className="text-slate font-normal text-[10.5px] mt-0.5">
                      {row.address}
                    </div>
                  )}
                </td>
                <td className="px-2.5 py-1.5 text-[12px] text-ink">{row.tenant_name || "—"}</td>
                <td
                  className="px-2.5 py-1.5 text-[12px] text-ink truncate"
                  title={row.association_type || ""}
                  data-testid={`associations-admin-type-${row.aid}`}
                >
                  {row.association_type || "—"}
                </td>
                <td className="px-2.5 py-1.5 text-[12px] font-mono tabular-nums text-slate">
                  {row.tenant_display_id || "—"}
                </td>
                {/* 2026-02-28 (Dave) — Units column centered under its
                    header (was right-aligned to match the numeric
                    convention; Dave prefers centered for this view). */}
                <td className="px-2.5 py-1.5 text-[12px] font-mono tabular-nums text-ink text-center">
                  {typeof row.unit_count === "number" ? row.unit_count : "—"}
                </td>
                {/* 2026-03-08 (Dave) — Users column: number of active
                    users credentialed on this association. Clickable
                    to drill through to Admin → Users pre-filtered to
                    this association (handoff via sessionStorage +
                    onScreenChange). Same centered mono formatting as
                    Units. */}
                <td
                  className="px-2.5 py-1.5 text-[12px] font-mono tabular-nums text-center"
                  data-testid={`associations-admin-users-${row.aid}`}
                >
                  <button
                    type="button"
                    onClick={() => {
                      try {
                        sessionStorage.setItem("ureserve:users-filter-aid", row.aid);
                        sessionStorage.setItem(
                          "ureserve:users-filter-name",
                          `${row.name}${row.tenant_name ? ` — ${row.tenant_name}` : ""}`,
                        );
                      } catch { /* sessionStorage disabled — filter simply won't preload */ }
                      if (typeof onScreenChange === "function") onScreenChange("Users");
                    }}
                    className="text-navy hover:text-coral hover:underline underline-offset-2 transition-colors disabled:opacity-40 disabled:no-underline disabled:cursor-default"
                    disabled={!row.user_count}
                    title={row.user_count ? `View the ${row.user_count} user${row.user_count === 1 ? "" : "s"} on ${row.name}` : "No users on this association yet"}
                    data-testid={`associations-admin-users-link-${row.aid}`}
                  >
                    {typeof row.user_count === "number" ? row.user_count : "—"}
                  </button>
                </td>
                <td className="px-2.5 py-1.5 text-center">
                  <TierChip tier={row.tier} />
                </td>
                <td className="px-2.5 py-1.5">
                  <StatusPill active={!!row.is_active} />
                </td>
                <td className="px-2.5 py-1.5 text-[11.5px] text-slate">{fmtDate(row.created_at)}</td>
                {/* 2026-02-28 (Dave, v3) — action cluster mirrors
                    the FirmsScreen drawer: Edit (pencil), soft
                    (De)Activate, Delete Permanently.  Same icons,
                    same border-hover treatment, same test-id
                    prefixes so muscle-memory carries over. */}
                <td className="px-2.5 py-1.5 text-center whitespace-nowrap">
                  <div className="inline-flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setEditing(row)}
                      className="p-1 border border-rule text-slate hover:border-navy hover:text-navy transition-colors rounded-[3px]"
                      title="Edit association"
                      data-testid={`associations-admin-edit-btn-${row.aid}`}
                    >
                      <Pencil size={11} />
                    </button>
                    <button
                      type="button"
                      onClick={() => doSoftToggle(row.aid, row.is_active)}
                      disabled={busyAid === row.aid}
                      className="p-1 border border-rule text-slate hover:border-navy hover:text-navy disabled:opacity-40 transition-colors rounded-[3px]"
                      title={row.is_active ? "Deactivate association (reversible)" : "Reactivate association"}
                      data-testid={`associations-admin-soft-toggle-${row.aid}`}
                    >
                      {busyAid === row.aid ? (
                        <Loader2 size={11} className="animate-spin" />
                      ) : row.is_active ? (
                        <PowerOff size={11} />
                      ) : (
                        <Undo2 size={11} />
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => setPurging({ aid: row.aid, name: row.name })}
                      disabled={busyAid === row.aid}
                      className="p-1 border border-rule text-slate hover:border-coral hover:text-coral disabled:opacity-40 transition-colors rounded-[3px]"
                      title="Delete association permanently (irreversible)"
                      data-testid={`associations-admin-purge-btn-${row.aid}`}
                    >
                      <Trash2 size={11} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <AddAssociationModal
          onClose={() => setShowCreate(false)}
          onCreated={async () => {
            // 2026-03-03 (Dave) — Stay on the Associations admin tab after
            // saving. Previously we auto-selected the new aid and jumped
            // to Dashboard; now the admin sees the newly-added row in
            // context and can continue triaging the list.
            setShowCreate(false);
            await load();
            try {
              window.dispatchEvent(new CustomEvent("ureserve:associations-changed"));
            } catch { /* no-op */ }
          }}
        />
      )}
      {editing && (
        <EditAssociationModal
          row={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await load();
            try {
              window.dispatchEvent(new CustomEvent("ureserve:associations-changed"));
            } catch { /* no-op */ }
          }}
        />
      )}
      {purging && (
        <PurgeAssociationConfirm
          assoc={purging}
          busy={busyAid === purging.aid}
          onCancel={() => setPurging(null)}
          onConfirm={(typed) => doPurge(purging.aid, typed)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// AddAssociationModal — lightweight cross-firm create flow reachable
// from the Admin → Associations header button. Keeps the essential
// fields (Firm, Name, ID, Address, Units, Opening Balance) and calls
// the same POST /admin/firms/{tid}/associations endpoint the drawer
// uses. Richer editing lives in FirmsScreen's drawer form so we
// deliberately do NOT duplicate the scenario-defaults block here.
// ---------------------------------------------------------------------------
function AddAssociationModal({ onClose, onCreated }) {
  useIdleExempt(true);
  const [firms, setFirms] = useState([]);
  const [firmsLoading, setFirmsLoading] = useState(true);
  const [form, setForm] = useState({
    tenant_id: "",
    name: "",
    tenant_display_id: "",
    address: "",
    unit_count: 0,
    current_fund_balance: 0,
    tier: "pro",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/firms`, { params: { include_inactive: false } });
        if (!cancelled) setFirms(Array.isArray(r.data) ? r.data : []);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || e.message || "Failed to load firms.");
      } finally {
        if (!cancelled) setFirmsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const submit = async () => {
    if (!form.tenant_id) { setError("Pick a firm."); return; }
    if (!form.name.trim()) { setError("Association name is required."); return; }
    setSaving(true); setError("");
    try {
      const res = await axios.post(
        `${API}/admin/firms/${form.tenant_id}/associations`,
        {
          name: form.name.trim(),
          tenant_display_id: form.tenant_display_id.trim() || undefined,
          address: form.address.trim() || undefined,
          unit_count: Number(form.unit_count) || 0,
          current_fund_balance: Number(form.current_fund_balance) || 0,
          tier: form.tier || "pro",
        },
      );
      onCreated && onCreated(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to create association.");
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: "rgba(20, 30, 45, 0.55)", backdropFilter: "blur(2px)" }}
      onClick={onClose}
      data-testid="associations-admin-add-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white shadow-2xl rounded-[3px] border border-navy flex flex-col"
        style={{ width: "min(520px, 92vw)" }}
        data-testid="associations-admin-add-modal"
      >
        <div
          className="flex items-center justify-between px-4 py-2.5 border-b border-rule"
          style={{ background: "#F7FAFC" }}
        >
          <span className="text-[13px] tracking-[0.14em] uppercase font-bold text-navy">
            New Association
          </span>
          <button
            type="button"
            onClick={onClose}
            className="text-slate hover:text-coral transition-colors"
            data-testid="associations-admin-add-close"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="px-4 py-3 flex flex-col gap-2 text-[11.5px]">
          {error && (
            <div className="px-2 py-1 border border-coral bg-[#FEF5F2] text-coral text-[11px] rounded-[3px]">
              {error}
            </div>
          )}
          <label className="flex flex-col gap-0.5">
            <span className="text-[9.5px] tracking-wide uppercase text-slate">Association Name *</span>
            <input
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              className="border border-rule px-2 py-1 outline-none focus:border-navy rounded-[3px]"
              data-testid="associations-admin-add-name"
              autoFocus
            />
          </label>
          <label className="flex flex-col gap-0.5">
            <span className="text-[9.5px] tracking-wide uppercase text-slate">Firm Name *</span>
            <select
              value={form.tenant_id}
              onChange={(e) => set("tenant_id", e.target.value)}
              className="border border-rule px-2 py-1 outline-none focus:border-navy rounded-[3px]"
              data-testid="associations-admin-add-firm"
              disabled={firmsLoading}
            >
              <option value="">{firmsLoading ? "Loading firms…" : "Pick a firm"}</option>
              {firms.map((f) => (
                <option key={f.tenant_id} value={f.tenant_id}>{f.name}</option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-0.5">
            {/* 2026-03-04 (Dave) — Per-association content tier. */}
            <span className="text-[9.5px] tracking-wide uppercase text-slate">Tier</span>
            <select
              value={form.tier || "pro"}
              onChange={(e) => set("tier", e.target.value)}
              className="border border-rule px-2 py-1 outline-none focus:border-navy rounded-[3px]"
              data-testid="associations-admin-add-tier"
            >
              <option value="pro">Pro</option>
              <option value="basic">Basic</option>
            </select>
          </label>
          <div className="grid grid-cols-2 gap-2">
            <label className="flex flex-col gap-0.5">
              <span className="text-[9.5px] tracking-wide uppercase text-slate">Display ID</span>
              <input
                value={form.tenant_display_id}
                onChange={(e) => set("tenant_display_id", e.target.value)}
                className="border border-rule px-2 py-1 outline-none focus:border-navy rounded-[3px]"
                data-testid="associations-admin-add-display-id"
                placeholder="auto"
              />
            </label>
            <label className="flex flex-col gap-0.5">
              <span className="text-[9.5px] tracking-wide uppercase text-slate">Units</span>
              <input
                type="number"
                min="0"
                value={form.unit_count}
                onChange={(e) => set("unit_count", e.target.value)}
                className="border border-rule px-2 py-1 outline-none focus:border-navy rounded-[3px] font-mono tabular-nums"
                data-testid="associations-admin-add-units"
              />
            </label>
          </div>
          <label className="flex flex-col gap-0.5">
            <span className="text-[9.5px] tracking-wide uppercase text-slate">Address</span>
            <input
              value={form.address}
              onChange={(e) => set("address", e.target.value)}
              className="border border-rule px-2 py-1 outline-none focus:border-navy rounded-[3px]"
              data-testid="associations-admin-add-address"
              placeholder="Optional"
            />
          </label>
          <label className="flex flex-col gap-0.5">
            <span className="text-[9.5px] tracking-wide uppercase text-slate">Opening Fund Balance</span>
            <input
              type="number"
              min="0"
              value={form.current_fund_balance}
              onChange={(e) => set("current_fund_balance", e.target.value)}
              className="border border-rule px-2 py-1 outline-none focus:border-navy rounded-[3px] font-mono tabular-nums"
              data-testid="associations-admin-add-opening-balance"
            />
          </label>
          <div className="text-[10.5px] text-slate italic mt-0.5">
            All scenario assumptions (inflation, interest, target, adjustment) seed to 0.
            Edit them under Firms → the firm's Associations drawer for the fuller form.
          </div>
        </div>

        <div
          className="flex items-center justify-end gap-2 px-4 py-2.5 border-t border-rule"
          style={{ background: "#F7FAFC" }}
        >
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1 text-[11.5px] font-semibold border border-navy text-navy bg-white rounded-[3px] hover:bg-navy-wash transition-colors"
            data-testid="associations-admin-add-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={saving || firmsLoading}
            className="inline-flex items-center gap-1 px-3 py-1 text-[11.5px] font-semibold bg-navy text-white border border-navy rounded-[3px] hover:bg-[#164066] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="associations-admin-add-submit"
          >
            {saving ? <Loader2 size={11} className="animate-spin" /> : <Plus size={12} strokeWidth={2.4} />}
            Create
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// EditAssociationModal — row-click editor for the Admin → Associations
// list.  PATCHes /admin/associations/{aid} with ONLY the fields the
// user actually changed (partial update).  Deliberately narrow field
// set: Name, Display ID, Address, Units, Active.  Richer editing
// (scenario defaults, plan year, opening balance) lives on the fuller
// FirmsScreen drawer form so this modal stays scannable.
// ---------------------------------------------------------------------------
function EditAssociationModal({ row, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: row.name || "",
    tenant_display_id: row.tenant_display_id || "",
    address: row.address || "",
    association_type: row.association_type || "",
    unit_count: row.unit_count ?? 0,
    tier: row.tier || "pro",
    is_active: !!row.is_active,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const submit = async () => {
    if (!form.name.trim()) { setError("Association name is required."); return; }
    setSaving(true); setError("");
    try {
      // Build a partial diff so we never overwrite untouched fields.
      const patch = {};
      if (form.name.trim() !== (row.name || "")) patch.name = form.name.trim();
      if (form.tenant_display_id.trim() !== (row.tenant_display_id || "")) {
        patch.tenant_display_id = form.tenant_display_id.trim() || null;
      }
      if ((form.address || "").trim() !== (row.address || "")) {
        patch.address = form.address.trim() || null;
      }
      if ((form.association_type || "") !== (row.association_type || "")) {
        patch.association_type = form.association_type || null;
      }
      if (Number(form.unit_count) !== (row.unit_count ?? 0)) {
        patch.unit_count = Number(form.unit_count) || 0;
      }
      if ((form.tier || "pro") !== (row.tier || "pro")) {
        patch.tier = form.tier || "pro";
      }
      if (form.is_active !== !!row.is_active) patch.is_active = form.is_active;
      if (Object.keys(patch).length === 0) {
        onClose && onClose();
        return;
      }
      await axios.patch(`${API}/admin/associations/${row.aid}`, patch);
      toast.success(`${form.name.trim()} saved.`);
      onSaved && onSaved();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to save.");
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: "rgba(20, 30, 45, 0.55)", backdropFilter: "blur(2px)" }}
      onClick={onClose}
      data-testid="associations-admin-edit-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white shadow-2xl rounded-[3px] border border-navy flex flex-col"
        style={{ width: "min(520px, 92vw)" }}
        data-testid="associations-admin-edit-modal"
      >
        <div
          className="flex items-center justify-between px-4 py-2.5 border-b border-rule"
          style={{ background: "#F7FAFC" }}
        >
          <div className="flex flex-col">
            <span className="text-[13px] tracking-[0.14em] uppercase font-bold text-navy">
              Edit Association
            </span>
            <span className="text-[10.5px] text-slate mt-0.5">
              {row.tenant_name || "—"}
              {row.tenant_display_id ? ` · ${row.tenant_display_id}` : ""}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate hover:text-coral transition-colors"
            data-testid="associations-admin-edit-close"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="px-4 py-3 flex flex-col gap-2 text-[11.5px]">
          {error && (
            <div className="px-2 py-1 border border-coral bg-[#FEF5F2] text-coral text-[11px] rounded-[3px]">
              {error}
            </div>
          )}
          <label className="flex flex-col gap-0.5">
            <span className="text-[9.5px] tracking-wide uppercase text-slate">Name *</span>
            <input
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              className="border border-rule px-2 py-1 outline-none focus:border-navy rounded-[3px]"
              data-testid="associations-admin-edit-name"
              autoFocus
            />
          </label>
          <div className="grid grid-cols-2 gap-2">
            <label className="flex flex-col gap-0.5">
              <span className="text-[9.5px] tracking-wide uppercase text-slate">Display ID</span>
              <input
                value={form.tenant_display_id}
                onChange={(e) => set("tenant_display_id", e.target.value)}
                className="border border-rule px-2 py-1 outline-none focus:border-navy rounded-[3px]"
                data-testid="associations-admin-edit-display-id"
                placeholder="auto"
              />
            </label>
            <label className="flex flex-col gap-0.5">
              <span className="text-[9.5px] tracking-wide uppercase text-slate">Units</span>
              <input
                type="number"
                min="0"
                value={form.unit_count}
                onChange={(e) => set("unit_count", e.target.value)}
                className="border border-rule px-2 py-1 outline-none focus:border-navy rounded-[3px] font-mono tabular-nums"
                data-testid="associations-admin-edit-units"
              />
            </label>
          </div>
          <label className="flex flex-col gap-0.5">
            <span className="text-[9.5px] tracking-wide uppercase text-slate">Address</span>
            <input
              value={form.address}
              onChange={(e) => set("address", e.target.value)}
              className="border border-rule px-2 py-1 outline-none focus:border-navy rounded-[3px]"
              data-testid="associations-admin-edit-address"
              placeholder="Optional"
            />
          </label>
          <label className="flex flex-col gap-0.5">
            {/* 2026-10-01 (Dave) — Taxonomy dropdown mirroring the
                enrollment form. "No type" = empty string = clears the
                field server-side. */}
            <span className="text-[9.5px] tracking-wide uppercase text-slate">Type</span>
            <select
              value={form.association_type || ""}
              onChange={(e) => set("association_type", e.target.value)}
              className="border border-rule px-2 py-1 outline-none focus:border-navy rounded-[3px] bg-white"
              data-testid="associations-admin-edit-type"
            >
              <option value="">— None —</option>
              <option value="Homeowners Association (HOA)">Homeowners Association (HOA)</option>
              <option value="Condominium Association">Condominium Association</option>
              <option value="Cooperative">Cooperative</option>
              <option value="Marina">Marina</option>
              <option value="Country Club">Country Club</option>
              <option value="Other">Other</option>
            </select>
          </label>
          <label className="flex flex-col gap-0.5">
            {/* 2026-03-04 (Dave) — Per-association content tier drives
                which optional tabs and dashboard widgets render for
                users viewing this association. */}
            <span className="text-[9.5px] tracking-wide uppercase text-slate">Tier</span>
            <select
              value={form.tier || "pro"}
              onChange={(e) => set("tier", e.target.value)}
              className="border border-rule px-2 py-1 outline-none focus:border-navy rounded-[3px]"
              data-testid="associations-admin-edit-tier"
            >
              <option value="pro">Pro</option>
              <option value="basic">Basic</option>
            </select>
          </label>
          <label className="inline-flex items-center gap-2 mt-1 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => set("is_active", e.target.checked)}
              className="accent-navy"
              data-testid="associations-admin-edit-active"
            />
            <span className="text-[11.5px] text-navy">Active</span>
          </label>
        </div>

        <div
          className="flex items-center justify-end gap-2 px-4 py-2.5 border-t border-rule"
          style={{ background: "#F7FAFC" }}
        >
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1 text-[11.5px] font-semibold border border-navy text-navy bg-white rounded-[3px] hover:bg-navy-wash transition-colors"
            data-testid="associations-admin-edit-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            className="inline-flex items-center gap-1 px-3 py-1 text-[11.5px] font-semibold bg-navy text-white border border-navy rounded-[3px] hover:bg-[#164066] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="associations-admin-edit-submit"
          >
            {saving ? <Loader2 size={11} className="animate-spin" /> : <Check size={12} strokeWidth={2.4} />}
            Save
          </button>
        </div>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// PurgeAssociationConfirm — typed-name confirmation gate for the
// hard-delete action.  Same UX language as the FirmsScreen drawer's
// purge modal (coral-bordered card, disabled Delete button until the
// association name is typed verbatim).  Kept local to this screen so
// the admin table stays self-contained.
// ---------------------------------------------------------------------------
function PurgeAssociationConfirm({ assoc, busy, onCancel, onConfirm }) {
  const [typed, setTyped] = useState("");
  const matches = typed.trim() === assoc.name;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      data-testid="associations-admin-purge-modal"
    >
      <div
        className="absolute inset-0"
        style={{ background: "rgba(15,42,68,0.55)" }}
        onClick={busy ? undefined : onCancel}
      />
      <div className="relative bg-white border-2 border-coral shadow-2xl w-[480px] p-5 rounded-[3px]">
        <div className="flex items-start gap-3 mb-3">
          <AlertTriangle size={22} className="text-coral shrink-0 mt-0.5" />
          <div>
            <div className="text-[15px] font-bold text-ink">
              Permanently delete this association?
            </div>
            <div className="text-[11.5px] text-slate mt-0.5">This cannot be undone.</div>
          </div>
        </div>
        <div className="text-[11.5px] text-ink mb-3 leading-relaxed">
          You are about to permanently delete{" "}
          <span className="font-semibold text-coral">{assoc.name}</span>.
          Every plan year, every element, every scenario, and every override
          under this association will be erased from the database. There is
          no restore.
        </div>
        <div className="text-[10.5px] text-slate uppercase tracking-wider font-semibold mb-1">
          Type the exact association name to confirm:
        </div>
        <input
          autoFocus
          type="text"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder={assoc.name}
          disabled={busy}
          className="w-full border border-rule bg-white px-2 py-1.5 text-[12px] font-mono outline-none focus:border-coral rounded-[3px]"
          data-testid="associations-admin-purge-typed"
        />
        <div className="flex justify-end gap-2 mt-4">
          <button
            onClick={onCancel}
            disabled={busy}
            className="px-3 py-1.5 text-[11.5px] border border-rule text-slate hover:border-navy hover:text-navy disabled:opacity-40 rounded-[3px]"
            data-testid="associations-admin-purge-cancel"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(typed)}
            disabled={!matches || busy}
            className={`px-3 py-1.5 text-[11.5px] font-semibold bg-coral text-white transition-all rounded-[3px]
              ${matches && !busy
                ? "ring-2 ring-[#EA6A47]/50 ring-offset-2 shadow-md hover:bg-[#E85A42]"
                : "opacity-40 cursor-not-allowed"}`}
            data-testid="associations-admin-purge-confirm"
          >
            {busy ? (
              <span className="flex items-center gap-1.5">
                <Loader2 size={11} className="animate-spin" /> Deleting…
              </span>
            ) : "Delete Permanently"}
          </button>
        </div>
      </div>
    </div>
  );
}

